import { useEffect, useState } from "react"

// How long the player stays open before folding into its orb. Long enough to read
// the chat name and reach for a control, short enough that it isn't still
// sitting there once you've started reading the sheet underneath.
export const AUTO_COLLAPSE_MS = 5000

/**
 * Opens expanded, then folds away on its own.
 *
 * The mini-player has two jobs that want opposite things: say what is running,
 * and get out of the way. It can't do both at once, so it does them in order —
 * the full card first, the bare orb once you've had a chance to see it. Nothing
 * to tap, and nothing left on screen that the user has to tidy up.
 *
 * Every interaction restarts the countdown, and while the user is actually
 * holding the player there is no countdown at all. Those are two different
 * guarantees and the second is the one that matters: a restart still leaves the
 * timer free to fire in the middle of a slow drag, and a player that folds itself
 * away under the finger moving it is the one failure that would feel broken
 * rather than merely eager.
 *
 * Starts expanded on every mount rather than resuming a stored state: mounting
 * already-collapsed would spend the moment this exists for. Which corner it sits
 * in IS remembered — that's a preference; being collapsed is a phase, and its
 * scope is one covering sheet.
 */
export function useAutoCollapse({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const [collapsed, setCollapsed] = useState(false)
  // Bumped by anything that counts as the user still using the player. It has no
  // meaning of its own — it exists to re-run the effect below, which restarts
  // the countdown.
  const [activity, setActivity] = useState(0)
  // True from the moment a finger lands on the player until it leaves.
  const [held, setHeld] = useState(false)

  useEffect(() => {
    if (!enabled || collapsed || held) return

    const id = window.setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS)
    return () => window.clearTimeout(id)
  }, [enabled, collapsed, activity, held])

  return {
    // Never collapsed where the behaviour is switched off, whatever the timer
    // did before it was — otherwise turning it off could strand the orb.
    collapsed: enabled && collapsed,
    expand: () => {
      setCollapsed(false)
      setActivity((count) => count + 1)
    },
    // "Still in use" — restart the countdown without changing what's showing.
    poke: () => setActivity((count) => count + 1),
    // Hold the countdown off entirely for the length of a gesture. Letting go
    // re-runs the effect, so the full five seconds start from the release.
    setHeld,
  }
}
